import { LoadingSpinner, WrapXs2a } from '@components/Generic'
import { observer } from 'mobx-react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'
import { TinkAfterCancelPopup } from '@components/TinkAfterCancelPopup/TinkAfterCancelPopup'
import { CANCEL_SURVEY_MICROPAYMENT } from '../constants/cancelSurvey'
import { API_URL, ONBOARDING_SERVICE_PATH } from '@/constants/apiUrl'

interface GetStatusResponse {
  status: 'pending' | string
  expiringAtSeconds: number
  timeToExpirationSeconds: number
}

export const MicropaymentFintecPage = observer(() => {
  const store = useContext(StoreContext)
  const { currentScenarioId, context } = store.ScenarioState
  const { language } = store.TranslationsState
  const { VideoIdPage: trans } = store.TranslationsState.translations
  const cancelSurveyScreenEnabledServiceList = context?.cancelSurveyScreenEnabledServiceList || []

  const [sdkInit, setSdkInit] = useState(false)
  const [xs2aSessionKey, setXs2aSessionKey] = useState('')
  const [afterClosePopupVisible, setAfterClosePopupVisible] = useState(false)
  const [expirationTime, setExpirationTime] = useState(-1)

  const tinkUrl = new URL(context?.startSessionUrl || '').origin
  const xs2a = window.xs2a

  useInterval(async () => {
    try {
      const { data } = await axiosInstance.get<GetStatusResponse>(`${tinkUrl}/tink/get-status`, {
        withCredentials: true,
      })

      if (data.status !== 'pending') {
        store.TrackingState.disposeKeepSessionAliveInterval()
        xs2a.abortTransaction()
      }

      const expTime = data.expiringAtSeconds * 1000

      setExpirationTime(expTime)
    } catch (error) {
      devLog(error)
    }
  }, 60000)

  useInterval(() => {
    if (expirationTime === -1) return

    const now = new Date().getTime()
    if (now > expirationTime) {
      store.TrackingState.disposeKeepSessionAliveInterval()
      xs2a.abortTransaction()
    }
  }, 1000)

  const getInit = async () => {
    if (!context?.startSessionUrl) return

    try {
      const startSessionResponse = await axiosInstance.post(
        context.startSessionUrl,
        {},
        {
          withCredentials: true,
        }
      )

      setXs2aSessionKey(startSessionResponse.data.wizardSessionKey)
    } catch (e) {
      devLog(e)
    }
  }

  const initSdk = async (): Promise<void> => {
    try {
      setSdkInit(true)
      const xs2sElement = document.getElementById('XS2A-Form')
      if (xs2aSessionKey && xs2aSessionKey.length && xs2a) {
        xs2a.lang(language)
        xs2sElement!.setAttribute('data-xs2a', xs2aSessionKey)
        handleXs2aEvents()
        xs2a.init()
      }
    } catch (e) {
      throw e
    }
  }

  const postTelemetry = useCallback(async (selectedBankName: string, selectedBankBIC: string) => {
    if (currentScenarioId !== 'localTests') {
      try {
        await axiosInstance.post(
          `${API_URL}/${ONBOARDING_SERVICE_PATH}/audit-log-gateway`,
          {
            type: 'event.onboarding-web.tink.BANK_SELECTED',
            payload: {
              bankName: selectedBankName,
              bic: selectedBankBIC,
            },
          },
          {
            withCredentials: true,
          }
        )
      } catch (e) {
        devLog(e)
      }
    }
  }, [])

  const handleXs2aEvents = () => {
    xs2a.finish(function () {
      handleComplete('success')
    })

    xs2a.error(function (errorCode, messages, recoverable) {
      if (
        !recoverable &&
        !cancelSurveyScreenEnabledServiceList.some((cs) => CANCEL_SURVEY_MICROPAYMENT.includes(cs))
      ) {
        handleComplete('error', errorCode)
      }
    })

    xs2a.abort(function () {
      if (
        cancelSurveyScreenEnabledServiceList.some((cs) => CANCEL_SURVEY_MICROPAYMENT.includes(cs))
      ) {
        setAfterClosePopupVisible(true)
      } else {
        forceRedirect(`${API_URL}/micropayment/complete?status=error&error=aborted`)
      }
    })

    xs2a.intermission(function () {
      handleIntermission()
    })

    xs2a.login(function () {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const bankInput = document?.getElementById('XS2A-bank_code') as HTMLInputElement

      const bankData = document?.getElementsByClassName(
        'xs2a-completion-result-active'
      )[0] as HTMLElement

      const bankName = bankData
        ?.getElementsByClassName('xs2a-result-line1')[0]
        ?.textContent?.replace(/\s+/g, '')

      const bicHTML = bankData
        ?.getElementsByClassName('xs2a-result-line2')[0]
        ?.textContent?.replace(/\s+/g, '')
      const bankBIC = bicHTML?.substring(bicHTML?.indexOf('(') + 1, bicHTML?.lastIndexOf(')'))

      postTelemetry(bankName, bankBIC)
    })
  }

  const handleClosePopup = (): void => {
    handleComplete('error', 'aborted')
  }

  const handleComplete = (status: string, error?: string): void => {
    if (error) {
      forceRedirect(`${API_URL}/micropayment/complete?status=${status}&error=${error}`)
    } else {
      forceRedirect(`${API_URL}/micropayment/complete?status=${status}`)
    }
  }

  const handleIntermission = () => {
    try {
      axiosInstance.get(`${API_URL}/micropayment/continue`, {
        withCredentials: true,
      })
    } catch (e) {
      throw e
    }
  }

  useEffect(() => {
    if (xs2aSessionKey !== '') {
      initSdk()
    }
  }, [xs2aSessionKey])

  useEffect(() => {
    getInit()
  }, [])

  return (
    <div className="xs2a__container width-560">
      <TinkAfterCancelPopup
        title={trans.feedbackPopupTitle}
        thankYouForYourFeedback={trans.thankYouForYourFeedback}
        sendButtonText={trans.send}
        cancelButtonText={trans.close}
        visibility={afterClosePopupVisible}
        handleVisibility={handleClosePopup}
      />
      <WrapXs2a display={sdkInit ? 'visible' : 'none'} />
      {!sdkInit && <LoadingSpinner />}
    </div>
  )
})
